import React, { SVGProps } from 'react';
import { cn } from '~/lib/utils';

export type IconName =
  // Material Symbols (font ligatures)
  | 'account'
  | 'activeCheckBox'
  | 'alternate_email'
  | 'arrowBack'
  | 'arrowDropDown'
  | 'badge'
  | 'checkCircle'
  | 'checkMark'
  | 'chevron'
  | 'chevron_right'
  | 'close'
  | 'darkMode'
  | 'database'
  | 'disableCheckBoxChatoshi'
  | 'disableCheckBoxHero'
  | 'edit'
  | 'emergency'
  | 'error'
  | 'hdrAuto'
  | 'info'
  | 'lightMode'
  | 'logout'
  | 'mail'
  | 'menu'
  | 'paid'
  | 'person'
  | 'photo_camera'
  | 'phone_iphone'
  | 'qr_code'
  | 'support'
  | 'sync'
  | 'threeDots'
  | 'upload'
  // Branding SVG icons
  | 'chatoshi'
  | 'chatoshiLogo'
  | 'chatoshiBadge'
  | 'errorPageChatoshi'
  | 'errorPageHero'
  | 'hero'
  | 'heroLogo'
  | 'heroBadge';

interface IconProps extends SVGProps<SVGSVGElement> {
  name: IconName;
}

const materialIcons: Partial<Record<IconName, string>> = {
  account: 'person',
  activeCheckBox: 'check_box',
  arrowBack: 'arrow_back',
  arrowDropDown: 'arrow_drop_down',
  checkCircle: 'check_circle',
  checkMark: 'check',
  chevron: 'chevron_right',
  close: 'close',
  darkMode: 'dark_mode',
  disableCheckBoxChatoshi: 'check_box_outline_blank',
  disableCheckBoxHero: 'check_box_outline_blank',
  edit: 'edit',
  emergency: 'emergency',
  error: 'error',
  hdrAuto: 'hdr_auto',
  info: 'info',
  lightMode: 'light_mode',
  logout: 'logout',
  menu: 'menu',
  photo_camera: 'photo_camera',
  sync: 'sync',
  threeDots: 'more_horiz',
  upload: 'upload',
};

const materialIconNames = new Set<IconName>([
  'alternate_email',
  'badge',
  'chevron_right',
  'database',
  'mail',
  'paid',
  'person',
  'phone_iphone',
  'qr_code',
  'support',
]);

const iconModules = import.meta.glob<boolean, string, { default: React.FC }>(
  [
    './icons/chatoshi.svg',
    './icons/chatoshiLogo.svg',
    './icons/chatoshiBadge.svg',
    './icons/errorPageChatoshi.svg',
    './icons/errorPageHero.svg',
    './icons/hero.svg',
    './icons/heroLogo.svg',
    './icons/heroBadge.svg',
  ],
  { query: '?react' },
);

const icons = Object.values(iconModules).reduce<
  Record<IconName, React.LazyExoticComponent<React.FC<SVGProps<SVGSVGElement>>>>
>(
  (accum, module) => {
    const name = module.name.split(/[/.]/)[3] as IconName;
    accum[name] = React.lazy(module);
    return accum;
  },
  {} as Record<IconName, React.LazyExoticComponent<React.FC<SVGProps<SVGSVGElement>>>>,
);

function Icon({ name, className, ...rest }: IconProps) {
  const materialIcon = materialIcons[name] ?? (materialIconNames.has(name) ? name : null);

  if (materialIcon) {
    return (
      <span
        aria-hidden="true"
        className={cn(
          'material-symbols-sharp shrink-0 select-none text-[22px] leading-none',
          className,
        )}
      >
        {materialIcon}
      </span>
    );
  }

  const { [name]: IconComponent } = icons;

  if (!IconComponent) {
    return null;
  }

  return <IconComponent className={className} {...rest} />;
}

export default Icon;
